import { observer } from "mobx-react";
import * as React from "react";
import Sys from "../../core/Sys";
import ComboBoxOption from "../../coreui/ComboBoxOption";
import { ErrorBadge } from "../../coreui/ErrorBadge";
import Icon from "../../coreui/Icon";
import { TableChildProps } from "../../coreui/Table";
import {
  CellFocusUtil,
  FocusCellRendererParams,
} from "../../coreui/table/CellFocusUtil";
import { CellUtil } from "../../coreui/table/CellUtil";
import Typography from "../../coreui/Typography";
import PaneRow, { RuntimeWidget } from "../../models/PaneRow";
import { AccessLevel } from "../AccessLevel";
import { DomainComboBox } from "../DomainComboBox";
import { FunctionName } from "../TableSummary";
import {
  GridColumnConfigProperties,
  RenderInlineProperties,
} from "./GridColumn";

interface ConfigProperties
  extends WidgetProperties,
    FocusCellRendererParams<string | null> {
  propagated: TableChildProps;
}

interface State {
  isErrorBadgeOpen?: boolean;
}

interface RuntimeProperties {
  accessLevel: AccessLevel;
  businessErrors: string[];
  selectedDisplayValue: string;
  selectedValue: string | null;
}

interface WidgetProperties {
  dataId: string;
  name: string;
  options: ComboBoxOption[];
}

export class DomainComboBoxColumnDisplay extends React.Component<
  ConfigProperties,
  State
> {
  public static readonly widgetType: string = "DomainComboBoxColumn";
  private readonly componentId: string;
  private readonly errorMessageId: string;

  public static getFilterText(
    column: GridColumnConfigProperties,
    propagated: TableChildProps,
    row: PaneRow
  ): string {
    const config = column.widgetProps as WidgetProperties;
    const widget = row.getWidgetT<string | null, RuntimeProperties>(
      column.name
    );

    const options = DomainComboBox.getOptions(
      config.options,
      widget.properties.selectedValue,
      widget.properties.selectedDisplayValue
    );

    const currentOption = options.find((o) => o.value === widget.value);

    return currentOption ? currentOption.display : "";
  }

  public static getSummaryValue(
    runtimeData: RuntimeWidget[],
    configProperties: ConfigProperties,
    functionName: FunctionName
  ): string | null {
    return runtimeData.filter((d) => d.value).length.toString();
  }

  public static renderInline(
    props: RenderInlineProperties
  ): JSX.Element | null {
    const config = props.column.widgetProps as WidgetProperties;
    const widget = props.row.getWidgetT<string | null, RuntimeProperties>(
      props.column.name
    );

    if (widget.properties.accessLevel <= AccessLevel.disabled) {
      return null;
    }

    const options = DomainComboBox.getOptions(
      config.options,
      widget.properties.selectedValue,
      widget.properties.selectedDisplayValue
    );

    const currentOption = options.find((o) => o.value === widget.value);

    if (!currentOption) {
      return null;
    }

    return (
      <Typography className={props.className} variant="body1">
        {currentOption.display}
      </Typography>
    );
  }

  public constructor(props: ConfigProperties) {
    super(props);

    this.state = { isErrorBadgeOpen: false };

    CellFocusUtil.subscribeToCellKeyboardFocusedEvent(
      props,
      this.onCellFocus,
      this.onCellBlur
    );

    this.componentId = `domain-combobox-column-display${Sys.nextId}`;
    this.errorMessageId = `${this.componentId}-error-message`;

    props.eGridCell.addEventListener("keydown", this.onCellKeyDown);
  }

  private onCellBlur = (): void => {
    this.setState({ isErrorBadgeOpen: false });
  };

  private onCellFocus = (): void => {
    this.setState({ isErrorBadgeOpen: true });
  };

  private onCellKeyDown = (event: KeyboardEvent): void => {
    CellUtil.customizeGridNavigation(event, this.props);

    if (event.key === " " && !this.props.api.getEditingCells().length) {
      this.props.api.startEditingCell({
        colKey: this.props.column!.getColId(),
        rowIndex: this.props.node.rowIndex!,
      });

      event.preventDefault();
      event.stopPropagation();
    }
  };

  private onCloseErrorBadge = (): void => {
    this.setState({ isErrorBadgeOpen: false });
  };

  private onOpenErrorBadge = (): void => {
    this.setState({ isErrorBadgeOpen: true });
  };

  public componentWillUnmount(): void {
    CellFocusUtil.unsubscribeToCellKeyboardFocusedEvent(
      this.props,
      this.onCellFocus,
      this.onCellBlur
    );
    this.props.eGridCell.removeEventListener("keydown", this.onCellKeyDown);
  }

  public render(): React.ReactNode {
    const row = this.props.data!;
    const widget = row.getWidgetT<string | null, RuntimeProperties>(
      this.props.name
    );

    const options = DomainComboBox.getOptions(
      this.props.options,
      widget.properties.selectedValue,
      widget.properties.selectedDisplayValue
    );

    let rowErrors: string | undefined = this.props.propagated.parentTable
      .getTable()
      .rowErrorMessages.get(row.rowKey);

    let value = null;

    if (widget.value) {
      const currentOption = options.find((o) => o.value === widget.value);
      value = currentOption ? currentOption.display : null;
    }

    CellUtil.setReadOnlyAttribute(
      this.props.eGridCell,
      widget.properties.accessLevel <= AccessLevel.actionable
    );

    const errors: string[] = row.hasChanges(this.props.name)
      ? []
      : [...widget.properties.businessErrors];

    let result: React.ReactNode = null;

    if (widget.properties.accessLevel >= AccessLevel.enterable) {
      if (errors.length) {
        const table = this.props.propagated.parentTable;

        CellUtil.setAriaAttributes(
          this.props.eGridCell,
          this.errorMessageId,
          true
        );

        rowErrors = `${errors.join(". ")}. ${rowErrors || ""}`;

        result = (
          <ErrorBadge
            errors={errors}
            isShort={row.isNew && table.isDocumentGrid}
            onClose={this.onCloseErrorBadge}
            onOpen={this.onOpenErrorBadge}
            open={this.state.isErrorBadgeOpen}
            suppressEdit={true}
          >
            <div
              aria-describedby={this.errorMessageId}
              aria-errormessage={this.errorMessageId}
              aria-invalid="true"
              role="textbox"
              style={{
                alignItems: "center",
                display: "flex",
                flex: "auto",
                margin: "0px 20px 0px 24px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  flex: "auto",
                  lineHeight: "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </div>
              <div className="cx-print">
                <Icon
                  fixedWidth
                  icon="fas fa-caret-down"
                  style={{
                    fontSize: 16,
                    marginLeft: ".4em",
                    marginRight: 1,
                  }}
                />
              </div>
            </div>
            <div id={this.errorMessageId} style={{ display: "none" }}>
              {rowErrors}
            </div>
          </ErrorBadge>
        );
      } else {
        CellUtil.setAriaAttributes(
          this.props.eGridCell,
          this.errorMessageId,
          !!rowErrors
        );

        result = (
          <div
            aria-describedby={rowErrors ? this.errorMessageId : undefined}
            aria-errormessage={rowErrors ? this.errorMessageId : undefined}
            aria-invalid={rowErrors ? true : false}
            role="textbox"
            style={{
              alignItems: "center",
              display: "flex",
              height: "100%",
              margin: "0 21px 0 24px",
            }}
          >
            <div
              style={{
                flex: "auto",
                lineHeight: "normal",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </div>
            <div className="cx-print">
              <Icon
                fixedWidth
                icon="fas fa-caret-down"
                style={{
                  fontSize: 16,
                  height: "auto",
                  marginLeft: ".4em",
                }}
              />
            </div>
            <div id={this.errorMessageId} style={{ display: "none" }}>
              {rowErrors}
            </div>
          </div>
        );
      }
    } else {
      CellUtil.setAriaAttributes(
        this.props.eGridCell,
        this.errorMessageId,
        !!rowErrors
      );

      result = (
        <div
          aria-describedby={rowErrors ? this.errorMessageId : undefined}
          aria-errormessage={rowErrors ? this.errorMessageId : undefined}
          aria-invalid={rowErrors ? true : false}
          role="textbox"
          style={{
            alignItems: "center",
            display: "flex",
            height: "100%",
            margin: "0px 24px",
          }}
        >
          <div
            style={{
              lineHeight: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {value}
          </div>
          <div id={this.errorMessageId} style={{ display: "none" }}>
            {rowErrors}
          </div>
        </div>
      );
    }

    return result;
  }
}

export default observer(DomainComboBoxColumnDisplay);
